import { getNextUpcoming } from '$tablet-app/src/app/calendar/common/actions';
import { CalendarItemType } from '@akin-home/amplify';
import { CalendarItemUnion } from '@akin-home/calendar/combined';
import { normalizeDateString } from '@akin-home/dates';
import { capitaliseFirstLetter } from '@akin-home/utils/strings';
import { addDays, endOfDay, format, isSameDay, startOfDay } from 'date-fns';
import {
  createCalendarChatItem,
  createOutputTextItem,
} from '../../ChatItem/functions';
import { AddChatItemFunction } from '../../types';
import { CheckCalendarItemsCommand } from '../types';

type Union = CalendarItemUnion;

type GetCalendarItemsParams = {
  type?: CalendarItemType;
  title?: string;
  from: string;
  to: string;
};

type Params = {
  command: CheckCalendarItemsCommand;
  getCalendarItems(params: GetCalendarItemsParams): Promise<Union[]>;
  addItems: AddChatItemFunction;
};

async function getItems({
  command,
  getCalendarItems,
}: Params): Promise<Union[]> {
  const { data } = command;
  try {
    const [filters, date] = assembleQueryFilter(data);
    const items = await getCalendarItems(filters);
    return filterResults(items, data, date);
  } catch (ex) {
    console.log(ex);
    return [];
  }
}

export async function handleCheckCalendarItemsCommand(
  params: Params
): Promise<{ error?: string } | undefined> {
  const result = CheckCalendarItemsCommand.safeParse(params.command);

  if (!result.success) {
    console.log('Error: ', result.error);
    return;
  }
  const { data } = result.data;
  try {
    const items = await getItems(params);

    if (items.length) {
      if (data.success_message?.length) {
        params.addItems(createOutputTextItem(data.success_message));
      }

      params.addItems(
        createCalendarChatItem({
          items: items.map((i) => ({ item_id: i.id, type: i.type })),
        })
      );
    } else {
      if (data.none_found_message?.length) {
        params.addItems(createOutputTextItem(data.none_found_message));
      }
    }
  } catch (ex) {
    console.log(ex);
    if (data.error_message) {
      params.addItems(createOutputTextItem(data.error_message));
    }
  }
}

function filterResults(
  input_items: CalendarItemUnion[],
  data: CheckCalendarItemsCommand['data'],
  date: Date
) {
  // Create a copy for immutability
  let items = [...input_items];
  const { until_time, time } = data;

  if (data.next_item) {
    const upcoming = getNextUpcoming(items);

    if (upcoming) {
      items = [upcoming];
    }
  } else if (until_time?.length) {
    // Get items before the specified time
    items = items.filter((item) => format(item.start, 'HH:mm') <= until_time);

    // Also, if time is specified, set the from time
    if (time?.length) {
      // Get the items after the start time
      items = items.filter((item) => format(item.start, 'HH:mm') >= time);
    }
  } else if (time?.length) {
    // If just the start time is specified, get items that match that time exactly
    items = items.filter((item) => format(item.start, 'HH:mm') === time);
  }

  return items.filter((item) => isSameDay(item.start, date));
}

function assembleQueryFilter(
  data: CheckCalendarItemsCommand['data']
): [GetCalendarItemsParams, Date] {
  let target_date = new Date();

  /** First check for date */
  if (data.date?.length) {
    if (data.date.toLowerCase() === 'tomorrow') {
      target_date = addDays(target_date, 1);
    } else if (data.date.toLowerCase() === 'yesterday') {
      target_date = addDays(target_date, -1);
    } else {
      target_date = new Date(normalizeDateString(data.date));
    }
  }

  const filters: GetCalendarItemsParams = {
    from: startOfDay(target_date).toISOString(),
    to: endOfDay(target_date).toISOString(),
  };

  /** Add type filtering if included in params */
  if (data.type?.length) {
    filters.type = capitaliseFirstLetter(data.type) as CalendarItemType;
  }

  if (data.title?.length) {
    filters.title = data.title;
  }

  return [filters, target_date];
}
